import { useQueries } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { ProgressionResponse, ProgressionRound, ProgressionSeries } from '../api/types'
import { DataState } from '../components/DataState'
import { ProgressionChart } from './ProgressionChart'

type Championship = 'drivers' | 'constructors'

/**
 * Who and what a replay leaves out, the table it scores on and whether it
 * turns the order over, as the API's query spells it.
 */
export type Removals = {
  exclude_drivers?: string
  exclude_constructors?: string
  exclude_rounds?: string
  scale?: string
  reverse?: boolean
}

function fetchProgression(
  year: number,
  championship: Championship,
  query: Removals & { top?: number; include?: string },
) {
  const params = { params: { path: { year }, query } }
  return championship === 'drivers'
    ? api.GET('/api/v1/seasons/{year}/progression/drivers', params)
    : api.GET('/api/v1/seasons/{year}/progression/constructors', params)
}

/**
 * One season's championship, round by round, above its table.
 *
 * Pins live in the URL as `?pin=a,b`, like every other choice on this site,
 * and go to the server as `include`: it alone knows the final order the
 * leaders are picked by. `removals` replays the season without them, which
 * is how the Formula 2 and ban pages ask for their own field.
 */
export function SeasonProgression({
  year,
  championship,
  removals = {},
}: {
  year: number
  championship: Championship
  removals?: Removals
}) {
  const [params, setParams] = useSearchParams()
  const pinned = (params.get('pin') ?? '').split(',').filter(Boolean)
  const query = {
    ...(pinned.length ? { include: pinned.join(',') } : {}),
    ...removals,
  }
  const progression = useApi<ProgressionResponse>(
    ['progression', year, championship, query],
    () => fetchProgression(year, championship, query),
  )

  const togglePin = (key: string) => {
    const next = pinned.includes(key) ? pinned.filter((item) => item !== key) : [...pinned, key]
    const updated = new URLSearchParams(params)
    if (next.length) updated.set('pin', next.join(','))
    else updated.delete('pin')
    setParams(updated, { replace: true })
  }

  return (
    <DataState query={progression} absent={null} empty={null}>
      {(data) => <ProgressionChart data={data} pinned={pinned} onTogglePin={togglePin} />}
    </DataState>
  )
}

/**
 * One line per season for one driver or constructor: the championship
 * points they held after each round, as the table read at the time.
 *
 * One request per season, because each total is the server's own -- the
 * frontend never adds points up. A constructor that ran two engines in a
 * season is two standings rows, so two lines. `removals` draws them inside a
 * category instead, every season on the same terms.
 */
export function CareerProgression({
  kind,
  slug,
  years,
  removals = {},
}: {
  kind: Championship
  slug: string
  years: number[]
  removals?: Removals
}) {
  const query = { include: slug, top: 1, ...removals }
  const queries = useQueries({
    queries: years.map((year) => ({
      queryKey: ['progression', year, kind, query],
      staleTime: Infinity,
      retry: false,
      queryFn: async () => {
        const { data, error } = await fetchProgression(year, kind, query)
        // A season with no constructors' championship (before 1958) has no line.
        if (error !== undefined || data === undefined) return null
        return data
      },
    })),
  })
  if (queries.some((query) => query.isPending)) return null

  const seasons = queries.flatMap((query) => (query.data ? [query.data] : []))
  const lines = seasons.flatMap((season) =>
    season.series
      .filter((line) => line.entity === slug)
      .map((line) => ({ year: season.year, line })),
  )
  if (lines.length === 0) return null

  const length = Math.max(...lines.map(({ line }) => line.points.length))
  const rounds: ProgressionRound[] = Array.from({ length }, (_, index) => ({
    round: index + 1,
    name: `Round ${index + 1}`,
    code: `R${index + 1}`,
    counts: true,
  }))
  // Newest first, so the seasons on screen get the hues and the end labels.
  const series: ProgressionSeries[] = lines
    .sort((a, b) => b.year - a.year)
    .map(({ year, line }, index) => ({
      ...line,
      key: `${year}-${line.key}`,
      name: line.engine_manufacturer ? `${year} · ${line.engine_manufacturer}` : String(year),
      rank: index + 1,
    }))
  const data: ProgressionResponse = {
    ...seasons[0]!,
    rounds,
    series,
    series_total: series.length,
  }

  return (
    <ProgressionChart
      data={data}
      caption="Championship points after each round, one line per season, as the table read at the time."
    />
  )
}
